// frontend/src/components/public-dosing/LeadGenBanner.tsx
import { useState, useEffect } from 'react';
import {
  Paper,
  Group,
  Text,
  TextInput,
  Button,
  CloseButton,
  Stack,
} from '@mantine/core';
import { IconMail } from '@tabler/icons-react';
// FIX C1: Import from centralized analytics utility
import { trackEvent } from '@/utils/analytics';

// FIX C6: localStorage key for banner persistence
const BANNER_STORAGE_KEY = 'reefdatahub_lead_banner_dismissed';

export function LeadGenBanner() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // FIX C6: Persist banner dismissal across sessions
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem(BANNER_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    // Don't track view if already dismissed
    if (dismissed) return;

    // Track banner view
    trackEvent('view_item', {
      item_name: 'lead_gen_banner',
      item_category: 'public_tools',
    });
  }, [dismissed]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Track signup attempt
      trackEvent('generate_lead', {
        item_name: 'email_signup',
        item_category: 'public_tools',
      });

      // SECURITY: Submit via backend proxy - NEVER expose API keys in frontend
      // The backend handles Beehiiv API authentication securely
      const response = await fetch('/api/public/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: 'dosing_calculator',
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Subscription failed');
      }

      setSubmitted(true);
      trackEvent('sign_up', {
        method: 'email',
        item_category: 'public_tools',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // FIX C6: Persist dismissal to localStorage
    try {
      localStorage.setItem(BANNER_STORAGE_KEY, 'true');
    } catch {
      // localStorage unavailable, still dismiss for this session
    }
    trackEvent('close', {
      item_name: 'lead_gen_banner',
      item_category: 'public_tools',
    });
  };

  if (dismissed) return null;

  return (
    <Paper
      data-banner
      p="md"
      radius="md"
      withBorder
      style={{
        background: 'linear-gradient(135deg, var(--mantine-color-blue-6) 0%, var(--mantine-color-cyan-6) 100%)',
        color: 'white',
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Stack gap="xs" style={{ flex: 1 }}>
          <Text fw={600} size="lg">
            Reef Data Hub is launching later this year!
          </Text>
          <Text size="sm" opacity={0.9}>
            Track your reef tank parameters, ICP results, dosing schedules, and more.
            Get notified when we launch.
          </Text>

          {!submitted ? (
            <form onSubmit={handleSubmit}>
              <Group gap="xs" mt="xs">
                <TextInput
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  leftSection={<IconMail size={16} />}
                  error={error}
                  style={{ flex: 1, maxWidth: 300 }}
                  styles={{
                    input: {
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: 'var(--mantine-color-dark-7)',
                    },
                  }}
                  aria-label="Email address for notifications"
                />
                <Button
                  type="submit"
                  variant="white"
                  color="blue"
                  loading={isSubmitting}
                >
                  Notify Me
                </Button>
              </Group>
            </form>
          ) : (
            <Text size="sm" fw={500} mt="xs">
              Thanks! We'll let you know when we launch.
            </Text>
          )}
        </Stack>

        <CloseButton
          onClick={handleDismiss}
          variant="subtle"
          c="white"
          aria-label="Dismiss banner"
        />
      </Group>
    </Paper>
  );
}
